import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ChevronRightIcon, DocumentTextIcon } from '@heroicons/react/solid';

/**
 * Shared modal shell for the "module complete" dialogs that appear throughout
 * the ADHD assessment workflow (CI, SSI, DD, PAD).
 *
 * Every assessment module surfaces a completion dialog once the clinician
 * finishes the final section.  The visual chrome — overlay, centred card,
 * title bar, optional description text, and a right-aligned action-button
 * row — is identical; only the body content and the specific action
 * buttons differ per module.
 *
 * Note: there is intentionally NO header "×" close affordance. Dismissing
 * the dialog must go through the explicit "Go back and edit" button in the
 * action row, which makes it visually obvious that closing the dialog
 * sends the clinician back to the form (it does not complete the module).
 *
 * This component extracts that shared chrome so each page only needs to supply:
 *   • title / description strings
 *   • body content via `children`
 *   • action buttons via the `actions` render slot
 *
 * Pre-styled button sub-components (CancelButton, PrimaryActionButton,
 * SecondaryActionButton, GenerateReportButton) are exported alongside the
 * dialog for callers to compose without duplicating Tailwind classes.
 *
 * @example
 * <ModuleCompletionDialog
 *   open={isDialogOpen && !isGenerating}
 *   onOpenChange={setIsDialogOpen}
 *   title="Clinical Interview Complete"
 *   description="Review your diagnostic decision before proceeding."
 *   actions={
 *     <>
 *       <CancelButton onClick={() => setIsDialogOpen(false)} />
 *       <GenerateReportButton onClick={handleGenerateReport} />
 *       <PrimaryActionButton label="Go to SSI" onClick={handleNavigate} />
 *     </>
 *   }
 * >
 *   <SummarySection title="Outcome:">{constructDiagnosisSummary()}</SummarySection>
 *   <DialogMessage>{getDialogMessage()}</DialogMessage>
 * </ModuleCompletionDialog>
 */
/**
 * @param {string} [maxHeightClass='max-h-[60vh]'] Tailwind max-height for the
 *   card. The default suits a recap plus a button row. Dialogs whose body is
 *   something the clinician has to fill in (two text boxes, say) pass a
 *   taller value such as 'max-h-[85vh]' so the whole form is on screen
 *   without scrolling; the body still scrolls if content outgrows it.
 */
function ModuleCompletionDialog({
  open,
  onOpenChange,
  title,
  description,
  width = 800,
  maxHeightClass = 'max-h-[60vh]',
  children,
  actions,
}) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
        <AlertDialog.Content
          className={`radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 max-w-[90vw] ${maxHeightClass} flex flex-col z-50 shadow-xl`}
          style={{ width: `${width}px` }}
        >
          {/* ── Fixed header ──────────────────────────────────────────────
              Title only — no "×" close button. Clinicians were
              misinterpreting "×" / "Cancel" as "the assessment step is
              done" and dismissing without triggering completion. The
              explicit "Go back and edit" button in the action footer is
              the single mouse path to leave the dialog. (Escape still
              works via the Radix Cancel handler.) */}
          <div className="shrink-0">
            <AlertDialog.Title className="text-xl font-bold mb-4">
              {title}
            </AlertDialog.Title>

            {description && (
              <AlertDialog.Description className="text-sm text-gray-600 mb-4">
                {description}
              </AlertDialog.Description>
            )}
          </div>

          {/* ── Scrollable body ────────────────────────────────────────────
              min-h-0 is essential so the flex child respects the parent's
              max-height constraint when overflow-y-auto kicks in. The
              light gray well (bg-gray-50) plus rounded corners separate
              the scrollable recap from the white header/footer so it is
              obvious there is more content below the fold. */}
          <div className="overflow-y-auto flex-1 min-h-0 bg-gray-50 rounded-lg p-3">
            {children}
          </div>

          {/* ── Fixed footer ──────────────────────────────────────────────
              Action buttons are always visible regardless of scroll
              position, with a subtle top border to separate from the
              scrollable content. flex-wrap handles narrow viewports. */}
          <div className="shrink-0 flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-100 mt-4">
            {actions}
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}


/* ---------------------------------------------------------------------------
 * Pre-styled button sub-components
 *
 * These mirror the Tailwind classes that were previously duplicated across
 * every module completion dialog.  Three visual tiers exist:
 *   • Primary   – solid violet background, white text (main CTA)
 *   • Secondary – outlined with a gray border (alternative action)
 *   • Cancel    – outlined, always leftmost in the button row
 *
 * All action buttons are wrapped in `AlertDialog.Action` (or .Cancel for the
 * cancel button) so Radix handles focus management and close-on-click.
 * --------------------------------------------------------------------------- */

const STYLES = {
  primary: 'bg-violet-900 text-white hover:bg-violet-800',
  secondary: 'border border-gray-300 hover:bg-gray-50 text-gray-900',
  disabled: 'bg-gray-300 text-gray-500 cursor-not-allowed',
};

/**
 * Default label used by `<CancelButton />` across every module completion
 * dialog (CI, SSI, DD, PAD). Exported so:
 *   • tests can assert against a single source of truth instead of
 *     duplicating the literal string in dozens of places, and
 *   • product/copy changes can be made in exactly one location without
 *     having to grep across the codebase.
 *
 * Keep the wording action-oriented and unambiguous: it must read as
 * "leave this dialog and go back to the form to make changes", NOT as
 * "the assessment step is finished".
 */
const DEFAULT_CANCEL_LABEL = 'Go back and edit';


/**
 * Dismiss button — always the leftmost action in a completion dialog.
 *
 * Wraps AlertDialog.Cancel so Radix closes the dialog on click and restores
 * focus correctly. The default label is "Go back and edit" (rather than the
 * generic "Cancel") because in the context of these module completion
 * dialogs, this control means "leave this confirmation summary and return
 * to the form to make changes" — it does NOT mean "this assessment step is
 * successfully finished". The longer, more descriptive label was introduced
 * after we observed clinicians misinterpreting "Cancel" as a completion
 * action and dismissing the dialog without triggering Generate Report /
 * navigate-to-next-stage, which left the parent module's status as
 * incomplete in Firestore.
 *
 * Callers can still override the label (e.g. label="Cancel") when the
 * generic word is more appropriate for their specific dialog.
 *
 * `whitespace-nowrap` prevents the multi-word default label from wrapping
 * mid-phrase on narrow viewports; the parent action row uses `flex-wrap`
 * to handle row overflow gracefully.
 *
 * @param {function} onClick – click handler
 * @param {string}   label   – button text (defaults to "Go back and edit")
 */
function CancelButton({ onClick, label = DEFAULT_CANCEL_LABEL }) {
  return (
    <AlertDialog.Cancel asChild>
      <button
        onClick={onClick}
        className="py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
      >
        {label}
      </button>
    </AlertDialog.Cancel>
  );
}


/**
 * Solid-violet action button for the primary CTA (e.g. "Go to SSI",
 * "Proceed to Diagnostic Decision").  Shows a right-chevron icon by default;
 * pass `icon` to override.
 *
 * @param {string}   label     – button text
 * @param {function} onClick   – click handler
 * @param {boolean}  disabled  – whether the button is disabled
 * @param {boolean}  secondary – if true, renders with outlined (secondary) styling
 * @param {React.ReactNode} icon – optional override icon; defaults to ChevronRightIcon
 * @param {'left'|'right'} iconPosition – which side of the label the icon appears on
 */
function PrimaryActionButton({
  label,
  onClick,
  disabled = false,
  secondary = false,
  icon,
  iconPosition = 'right',
}) {
  const style = disabled
    ? STYLES.disabled
    : secondary
      ? STYLES.secondary
      : STYLES.primary;

  const iconElement = icon !== undefined
    ? icon
    : <ChevronRightIcon className="w-5 h-5 ml-1" aria-hidden="true" />;

  return (
    <AlertDialog.Action asChild>
      <button
        disabled={disabled}
        onClick={(e) => { if (!disabled) onClick?.(e); }}
        className={`flex items-center px-4 py-2 mb-2 font-semibold rounded-md transition-all duration-300 ${style}`}
      >
        {iconPosition === 'left' && iconElement}
        <span>{label}</span>
        {iconPosition === 'right' && iconElement}
      </button>
    </AlertDialog.Action>
  );
}


/**
 * Outlined action button used when two navigation options are available and
 * one needs to be visually de-emphasised relative to the primary CTA.
 *
 * Functionally identical to PrimaryActionButton with `secondary={true}`,
 * provided as a semantic convenience so callers read more naturally.
 */
function SecondaryActionButton({ label, onClick, disabled = false, icon, iconPosition = 'right' }) {
  return (
    <PrimaryActionButton
      label={label}
      onClick={onClick}
      disabled={disabled}
      secondary
      icon={icon}
      iconPosition={iconPosition}
    />
  );
}


/**
 * "Generate Report" button with the DocumentTextIcon.
 * Renders as primary (violet) or secondary (outlined) depending on the
 * `secondary` prop — the caller controls this based on workflow config
 * (e.g. MDT Review orgs show Generate Report as the primary action).
 */
function GenerateReportButton({ onClick, disabled = false, secondary = false, savingLabel }) {
  const style = disabled
    ? STYLES.disabled
    : secondary
      ? STYLES.secondary
      : STYLES.primary;

  return (
    <AlertDialog.Action asChild>
      <button
        disabled={disabled}
        onClick={(e) => { if (!disabled) onClick?.(e); }}
        className={`flex items-center px-4 py-2 mb-2 font-semibold rounded-md transition-all duration-300 ${style}`}
      >
        <DocumentTextIcon className="w-5 h-5 mr-1" aria-hidden="true" />
        <span>{disabled && savingLabel ? savingLabel : 'Generate Report'}</span>
      </button>
    </AlertDialog.Action>
  );
}


/* ---------------------------------------------------------------------------
 * Body content helpers
 *
 * Small composable pieces for the most common patterns found inside the
 * completion dialog body (outcome summary, italic guidance message).
 * Pages with more complex body content (e.g. DD "next steps" textarea,
 * SSI DF recap panels) can use plain JSX in `children` instead.
 * --------------------------------------------------------------------------- */

/**
 * Labelled summary section — typically used for "Outcome:" or
 * "Your Recommendations:" blocks.
 */
function SummarySection({ title, children }) {
  return (
    <div className="mb-6">
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="text-gray-700">{children}</div>
    </div>
  );
}

/**
 * Italic guidance message shown below the summary and above the action
 * buttons (e.g. "Please review your selections. Are you happy to proceed?").
 */
function DialogMessage({ children }) {
  return (
    <p className="mb-6 text-sm italic text-gray-700">
      {children}
    </p>
  );
}


export default ModuleCompletionDialog;
export {
  CancelButton,
  PrimaryActionButton,
  SecondaryActionButton,
  GenerateReportButton,
  SummarySection,
  DialogMessage,
  STYLES,
  DEFAULT_CANCEL_LABEL,
};
